/**
 * <SamTile> — Sam, posé sur l'écran d'une activité (molecule).
 *
 * La tuile ronde que l'élève touche pour ouvrir la conversation. C'est sa tête dans un disque,
 * son nœud bleu dessous (`<ChampionAvatar>`) : la même forme que la capsule du tuteur, dans la
 * plus grande taille du livre. Sa couleur reste un costume : elle peint son nœud, jamais la
 * tuile. L'ancienne tuile était un aplat bleu ; ici c'est l'ombre des surfaces flottantes qui la
 * détache de l'écran.
 *
 * Fermée, elle ne dit rien d'autre que « Sam est là ». Quand il a parlé et que l'élève n'a pas
 * ouvert, un point (`unread`) le signale : il dit qu'il y a du neuf, pas combien.
 *
 * Elle ne se place jamais elle-même et rien n'y bouge : l'écran qui la porte la pose (voir
 * `<SamFloatingTile>`).
 *
 * Composes DS pieces only — `<ChampionAvatar>`, `<NotificationDot>`, `<FocusRing>`.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { FocusRing, focusRingHost } from "../../atoms/FocusRing"
import { NotificationDot } from "../../atoms/NotificationDot"
import { shadowStyle } from "../../utils/shadow"
import { SAM_TILE_SIZE } from "./samTileGeometry"

export { SAM_TILE_SIZE }

/** Le point, en proportion de la tuile : 14 px à 72, et son retrait depuis le coin de la boîte. */
const DOT_RATIO = 0.2
const DOT_INSET = 0.05

const TEXTS = {
    open: "Ouvrir la conversation avec Sam",
    openUnread: "Ouvrir la conversation avec Sam, nouveau message",
}

type PressableVisualState = { pressed: boolean; focused?: boolean }

export type SamTileProps = {
    onPress: () => void
    /** Sam a parlé et l'élève n'a pas ouvert : le point. Défaut `false`. */
    unread?: boolean
    /** Diamètre en px, une taille du livre. Défaut `SAM_TILE_SIZE`. */
    size?: number
    /**
     * Sam ne répond pas au toucher, ni au clavier (le visage de derrière, dans le tandem). Défaut
     * `false`.
     */
    disabled?: boolean
    testID?: string
}

export function SamTile(props: SamTileProps) {
    const { onPress, unread = false, size = SAM_TILE_SIZE, disabled = false, testID } = props

    return (
        <Pressable
            testID={testID}
            onPress={onPress}
            disabled={disabled}
            focusable={!disabled}
            accessibilityRole="button"
            accessibilityLabel={unread ? TEXTS.openUnread : TEXTS.open}
            style={({ pressed }: PressableVisualState) => [
                styles.tile(size),
                focusRingHost,
                pressed ? styles.pressed : undefined,
            ]}
        >
            {({ focused }: PressableVisualState) => (
                <>
                    <FocusRing visible={focused === true} radius={size / 2} />
                    <ChampionAvatar name="sam" size={size} />
                    {unread && (
                        <View style={styles.dot(size)}>
                            <NotificationDot size={Math.round(size * DOT_RATIO)} ringColor="white" />
                        </View>
                    )}
                </>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Le disque blanc sous la tête donne un corps à l'ombre ; le liseré de l'avatar le recouvre. */
    tile: (size: number) => ({
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: theme.colors.white,
        ...shadowStyle(theme, "floating"),
    }),
    pressed: {
        transform: [{ scale: 0.98 }],
    },
    /** Sur le bord du disque, en haut à droite : pas au coin de sa boîte, où il flotterait à côté. */
    dot: (size: number) => ({
        position: "absolute" as const,
        top: size * DOT_INSET,
        right: size * DOT_INSET,
    }),
}))
